import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';

import { ApiError } from '../api/client';
import { getDashboard } from '../api/endpoints/dashboard';
import type { ChargePaymentMethod } from '../api/schemas';
import { paymentMethodLabel } from '../lib/paymentMethods';
import { BookingRow } from '../components/BookingRow';
import { DonutChart, type DonutSlice } from '../components/DonutChart';
import { ErrorBanner } from '../components/ErrorBanner';
import { LoadingState } from '../components/LoadingState';
import { PageHeader } from '../components/PageHeader';
import { ReservationLinkCard } from '../components/ReservationLinkCard';
import { Count, Money } from '../components/Figures';
import { LatestFromTeam } from '../features/intelligence/LatestFromTeam';
import { useBusiness } from '../lib/useBusiness';
import { formatMoneyCents } from '../lib/format';

const DASHBOARD_REFETCH_INTERVAL_MS = 60_000;

/** Fixed ring order: orange and yellow never sit adjacent (see chart.css). */
const METHOD_RING_ORDER: ChargePaymentMethod[] = ['efectivo', 'tarjeta', 'otro', 'yappy', 'transferencia', 'banco_aliado'];

export function DashboardPage() {
  const dashboardQuery = useQuery({
    queryKey: ['business-dashboard'],
    queryFn: getDashboard,
    refetchInterval: DASHBOARD_REFETCH_INTERVAL_MS,
  });
  const dashboard = dashboardQuery.data;

  const business = useBusiness();
  return (
    <main>
      <PageHeader title="Inicio" description="El día de un vistazo." />
      {business?.isSuspended ? (
        <p className="notice" role="status">
          Tu negocio está en revisión. Te avisamos por correo cuando esté activo para tus clientes; mientras, completa tu perfil.
        </p>
      ) : null}
      <ReservationLinkCard />
      {dashboardQuery.isLoading ? <LoadingState label="Cargando el día…" /> : null}
      {dashboardQuery.isError ? (
        <>
          <ErrorBanner message={errorMessage(dashboardQuery.error)} />
          <button type="button" className="btn small secondary" disabled={dashboardQuery.isFetching} onClick={() => void dashboardQuery.refetch()}>Reintentar resumen</button>
        </>
      ) : null}
      <LatestFromTeam />
      {dashboard ? (
        <>
          <section aria-label="Hoy" className="section">
            <h2 className="section-label">Hoy</h2>
            <div className="group rows">
              <div className="row">
                <p className="row-title grow">Reservas</p>
                <p className="amount">
                  <Count value={dashboard.today.bookingsTotal} />
                </p>
              </div>
              <div className="row">
                <p className="row-title grow">Por cerrar</p>
                <p className="amount">
                  <Count value={dashboard.today.porCerrar} />
                </p>
              </div>
              <div className="row">
                <p className="row-title grow">Ingresos</p>
                <p className="amount">
                  <Money cents={dashboard.today.revenueCents} />
                </p>
              </div>
            </div>
          </section>

          <section aria-label="Este mes" className="section">
            <h2 className="section-label">Este mes</h2>
            {dashboard.month.byMethod.length === 0 ? (
              <p className="row-sub">Aún no hay cobros este mes.</p>
            ) : (
              <div className="donut-flex group">
                <DonutChart
                  slices={ringSlices(dashboard.month.byMethod)}
                  // The ring is Cobros by method, so its center is their sum;
                  // Ingresos (which also counts visits without a Cobro) gets
                  // its own row so the ring never claims a total it can't show.
                  centerValue={formatMoneyCents(cobradoCents(dashboard.month.byMethod))}
                  centerLabel="Cobrado"
                />
                <div className="donut-legend">
                  <div className="rows">
                    {ringSlices(dashboard.month.byMethod).map((slice) => (
                      <div className="row" key={slice.key}>
                        <p className="row-sub grow">
                          <span
                            className="donut-swatch"
                            style={{ background: `var(${slice.colorVar})` }}
                          />
                          {slice.label}
                        </p>
                        <p className="amount">{formatMoneyCents(slice.valueCents)}</p>
                      </div>
                    ))}
                    <div className="row">
                      <p className="row-sub grow">Ingresos</p>
                      <p className="amount">
                        <Money cents={dashboard.month.ingresosCents} />
                      </p>
                    </div>
                    <div className="row">
                      <p className="row-sub grow">Gastos</p>
                      <p className="amount">
                        <Money cents={dashboard.month.gastosCents} />
                      </p>
                    </div>
                    <div className="row">
                      <p className="row-title grow">Neto</p>
                      <p className="amount">
                        <Money cents={dashboard.month.netoCents} />
                      </p>
                    </div>
                  </div>
                  <Link to="/finanzas" className="linklike">
                    Ver Finanzas
                  </Link>
                </div>
              </div>
            )}
          </section>

          <section aria-label="Ahora sigue" className="section">
            <h2 className="section-label">Ahora sigue</h2>
            {dashboard.today.next.length === 0 ? (
              <p className="row-sub">Nada más agendado por hoy.</p>
            ) : (
              <ul className="rows agenda-list">
                {dashboard.today.next.map((booking) => (
                  <BookingRow key={booking.id} booking={booking} />
                ))}
              </ul>
            )}
            <p>
              <Link to="/reservas" className="linklike">
                Ver todas las reservas
              </Link>
            </p>
          </section>
        </>
      ) : null}
    </main>
  );
}

function cobradoCents(byMethod: { method: ChargePaymentMethod; totalCents: number }[]): number {
  return byMethod.reduce((sum, entry) => sum + entry.totalCents, 0);
}

/** Methods in fixed ring order, only the ones with money in them. */
function ringSlices(byMethod: { method: ChargePaymentMethod; totalCents: number }[]): DonutSlice[] {
  const totals = new Map(byMethod.map((entry) => [entry.method, entry.totalCents]));
  return METHOD_RING_ORDER.filter((method) => (totals.get(method) ?? 0) > 0).map((method) => ({
    key: method,
    label: paymentMethodLabel(method),
    valueCents: totals.get(method) ?? 0,
    colorVar: `--chart-pago-${method}`,
  }));
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return 'No pudimos cargar el resumen.';
}
